import { Images } from '@/assets/images';
import { BusinessType, DocTypeEnum, REGEX } from '@/constant';
import { cn, dayjs, formatDate, insertObjectIfElse, map } from '@/utils';

import { range } from 'ramda';
import { isNotNilOrEmpty } from 'ramda-adjunct';

import {
	renderValuesApplicantStatus,
	renderValuesFranchiseStatus,
} from '../define-document-write.helper';
import { CheckboxType, FormConfig } from '../define-document-write.type';

export const getInitialArrayKeyValue = (
	to: number,
	defaultValue: string | boolean
) =>
	range(0, to).map((_, i) => ({
		key: i.toString(),
		value: defaultValue,
	}));

const initializeValue = {
	attractionPlanRegistDate: '',
	attractionPlanGovernor: '',
	position: '', // '군수',
	attractionPlanRegistName: '',
	attractionPlanSignature: '',
	attractionPlanLocalGovernment: '',
	attractionPlanDepartment: '',
	attractionPlanDepartmentPhone: '',
	attractionPlanEmployeeName: '',
	attractionPlanEmployeePhone: '',
	attractionPlanOperationManagerOfficerName: '',
	attractionPlanOperationManagerOfficerPhone: '',
	attractionPlanOperationManagerComtorName: '',
	attractionPlanOperationManagerComtorPhone: '',
	attractionPlanAgricultural: map(range(0, 4), () => ({
		count: '0',
		workerNumber: '0',
	})),
	attractionPlanFishery: map(range(0, 4), () => ({
		count: '0',
		workerNumber: '0',
	})),
	attractionPlanSupplyManpower: [],
	attractionPlanManpowerManagement: [],
	attractionPlanIncentivesGranted: '',
	attractionPlanCriteriaRecruit: '',
	attractionPlanSolutionPeriod: '',
	attractionPlanSolutionMethod: '',
	attractionPlanSolutionDetail: '',
	attractionPlanMedicalSupport: '',
	attractionPlanTrainingPeriod: '',
	attractionPlanTrainingMethod: '',
	attractionPlanTrainingDetail: '',
	attractionPlanPreventionUnauthDept: '',
	attractionPlanPreventionUnauthDeptForeigner: '',
	attractionPlanPreventionUnauthDeptEmployer: '',
	attractionPlanPreventionUnauthDeptGovernment: '',
	attractionPlanPreventionUnauthDeptEtc: '',
	attractionPlanCovid19Plan: getInitialArrayKeyValue(4, ''),
	// attractionPlanCovid19PlanVaccination: '',
	// attractionPlanCovid19PlanFacility: '',
	// attractionPlanCovid19PlanBear: '',
	// attractionPlanCovid19PlanAction: '',
	initSuccess: false,
};

type IValues = typeof initializeValue;

export const ATTRACTION_PLAN_CONFIG: FormConfig<IValues, DocTypeEnum.PM1_04> = {
	initializeValue,
	title: '계절근로자 유치 계획서',
	mapPropToValues({ refTableName, detailData, myPageData }) {
		const { unitInfo, franchiseStatus } = detailData;
		return {
			attractionPlanLocalGovernment: myPageData?.unitName || '',
			...insertObjectIfElse(
				myPageData?.isWorkerMasterRole,
				{
					attractionPlanDepartment: unitInfo?.name || '',
				},
				{
					attractionPlanDepartment: myPageData?.departmentName || '',
				}
			),
			...insertObjectIfElse(
				myPageData?.isWorkerMasterRole,

				{
					attractionPlanEmployeeName: unitInfo?.managerName || '',
				},
				{ attractionPlanEmployeeName: myPageData?.name || '' }
			),
			attractionPlanAgricultural: map(range(0, 4), (index) => ({
				count: isNotNilOrEmpty(franchiseStatus)
					? renderValuesFranchiseStatus(
							index,
							BusinessType.AGRICULTURAL,
							franchiseStatus
						)?.toString()
					: '0',
				workerNumber: isNotNilOrEmpty(franchiseStatus)
					? renderValuesApplicantStatus(
							index,
							BusinessType.AGRICULTURAL,
							franchiseStatus
						)?.toString()
					: '0',
			})),
			attractionPlanFishery: map(range(0, 4), (index) => ({
				count: isNotNilOrEmpty(franchiseStatus)
					? renderValuesFranchiseStatus(
							index,
							BusinessType.FISHING,
							franchiseStatus
						)?.toString()
					: '0',
				workerNumber: isNotNilOrEmpty(franchiseStatus)
					? renderValuesApplicantStatus(
							index,
							BusinessType.FISHING,
							franchiseStatus
						)?.toString()
					: '0',
			})),

			attractionPlanRegistDate: formatDate(dayjs()),
		};
	},
	pages: [
		{
			background: {
				src: Images.attractionPlan1Highlight,
				className: 'template',
				dataSource: Images.attractionPlan1,
			},
			textAreas: [
				(values, index) => ({
					name: 'attractionPlanGovernor',
					maxHeight: 28,
					maxLength: 8,
					className:
						'text-[14px] text-center focus:bg-white bg-transparent !h-[28px]',
					regex: REGEX.ALLOW_KR_SPACE,
					containerClassName: cn(
						'absolute top-[22.3%] left-[425px] w-[116px] z-[1]',
						values?.attractionPlanGovernor && '!bg-white'
					),
				}),
				(values, index) => ({
					name: 'position',
					maxHeight: 28,
					maxLength: 2,
					className: 'text-[14px] text-left focus:bg-white !h-[28px]',
					regex: REGEX.ALLOW_KR_SPACE,
					containerClassName: cn(
						'absolute top-[22.3%] left-[513px] w-[30px]',
						values?.attractionPlanGovernor && '!bg-white'
					),
					readOnly: true,
				}),
				{
					name: 'attractionPlanLocalGovernment',
					maxHeight: 26,
					maxLength: 30,
					containerClassName: 'absolute top-[31.41%] left-[23.8%] w-[494px]',
				},
				(values, index) => ({
					name: 'attractionPlanRegistName',
					maxHeight: 24,
					maxLength: 5,
					className: 'focus:bg-white !h-[24px] text-center',
					containerClassName: cn(
						'absolute top-[22.4%] left-[540px] w-[72px]',
						values?.attractionPlanRegistName && '!bg-white'
					),
					regex: REGEX.ALLOW_KR,
				}),
				(values, index) => ({
					name: 'attractionPlanDepartment',
					maxHeight: 26,
					maxLength: 38,
					className: 'focus:bg-white !h-full pt-[5px]',
					containerClassName: cn(
						'absolute top-[34.2%] left-[23.8%] w-[494px]',
						values?.attractionPlanDepartment && '!bg-white'
					),
				}),
				(values, index) => ({
					name: 'attractionPlanEmployeeName',
					maxHeight: 26,
					maxLength: 38,
					className: 'focus:bg-white !h-full pt-[5px]',
					containerClassName: cn(
						'absolute top-[36.9%] left-[23.8%] w-[494px]',
						values?.attractionPlanEmployeeName && '!bg-white'
					),
				}),
				(values, index) => ({
					name: 'attractionPlanOperationManagerOfficerName',
					maxHeight: 120,
					maxLength: 300,
					containerClassName: cn(
						'absolute top-[39.8%] left-[185px] w-[498px]',
						values?.attractionPlanOperationManagerOfficerName && '!bg-white'
					),
					regex: REGEX.ALLOW_KR_EN_NUMBER_SPECIAL_CHARS,
				}),
				(values, index) => ({
					repeat: 4,
					jsonKey: 'attractionPlanAgricultural',
					name: 'count',
					maxHeight: 27,
					maxLength: 30,
					containerClassName: 'absolute left-[31.6%] w-[200px]',
					'data-tabIndex': index,
					boxStyle: {
						top: `${692 + 29 * index}px`,
					},
					regex: REGEX.ALLOW_NUMBER,
				}),
				(values, index) => ({
					repeat: 4,
					jsonKey: 'attractionPlanAgricultural',
					name: 'workerNumber',
					maxHeight: 27,
					maxLength: 30,
					containerClassName: 'absolute left-[468px] w-[200px]',
					'data-tabIndex': index,
					boxStyle: {
						top: `${692 + 29 * index}px`,
					},
					regex: REGEX.ALLOW_NUMBER,
				}),
				(values, index) => ({
					repeat: 4,
					jsonKey: 'attractionPlanFishery',
					name: 'count',
					maxHeight: 27,
					maxLength: 30,
					containerClassName: 'absolute left-[31.6%] w-[200px]',
					'data-tabIndex': index,
					boxStyle: {
						top: `${897 + 29 * index}px`,
					},
					regex: REGEX.ALLOW_NUMBER,
				}),
				(values, index) => ({
					repeat: 4,
					jsonKey: 'attractionPlanFishery',
					name: 'workerNumber',
					maxHeight: 27,
					maxLength: 30,
					containerClassName: 'absolute left-[468px] w-[200px]',
					'data-tabIndex': index,
					boxStyle: {
						top: `${897 + 29 * index}px`,
					},
					regex: REGEX.ALLOW_NUMBER,
				}),
			],
			datePickers: [
				{
					name: 'attractionPlanRegistDate',
					segmentedInputs: true,
					style: { fontSize: '14px' },
					format: 'YYYY      MM       DD',
					// style: { marginTop: -5 },
					containerClassName: 'absolute top-[20.2%] left-[304px]',
				},
			],
			cachetForms: [
				{
					className: 'absolute top-[20.7%] left-[77%] w-[70px] h-[70px]',
				},
			],
		},
		{
			background: {
				src: Images.attractionPlan2Highlight,
				className: 'template',
				dataSource: Images.attractionPlan2,
			},
			textAreas: [
				(values, index) => ({
					name: 'attractionPlanIncentivesGranted',
					maxHeight: 115,
					maxLength: 275,
					containerClassName: cn(
						'absolute w-[595px] h-[35px] top-[40%] left-[10.8%]',
						values.attractionPlanIncentivesGranted && 'bg-white'
					),
					regex: REGEX.ALLOW_KR_EN_NUMBER_SPECIAL_CHARS,
				}),
				(values, index) => ({
					name: 'attractionPlanCriteriaRecruit',
					maxHeight: 72,
					maxLength: 184,
					containerClassName: cn(
						'absolute w-[595px] h-[35px] top-[57.2%] left-[10.8%]',
						values.attractionPlanCriteriaRecruit && 'bg-white'
					),
					regex: REGEX.ALLOW_KR_EN_NUMBER_SPECIAL_CHARS,
				}),
				{
					name: 'attractionPlanSolutionPeriod',
					maxHeight: 32,
					maxLength: 30,
					containerClassName: 'absolute w-[186px] top-[819px] left-[86px]',
					regex: REGEX.ALLOW_KR_EN_NUMBER,
				},
				{
					name: 'attractionPlanSolutionMethod',
					maxHeight: 32,
					maxLength: 30,
					containerClassName: 'absolute w-[186px] top-[819px] left-[289px]',
					regex: REGEX.ALLOW_KR_EN_NUMBER,
				},
				{
					name: 'attractionPlanSolutionDetail',
					maxHeight: 32,
					maxLength: 30,
					containerClassName: 'absolute w-[186px] top-[819px] left-[492px]',
					regex: REGEX.ALLOW_KR_EN_NUMBER,
				},
				(values, index) => ({
					name: 'attractionPlanMedicalSupport',
					maxHeight: 90,
					maxLength: 230,
					containerClassName: cn(
						'absolute w-[595px] h-[35px] top-[83.1%] left-[10.8%]',
						values.attractionPlanMedicalSupport && 'bg-white'
					),
					regex: REGEX.ALLOW_KR_EN_NUMBER_SPECIAL_CHARS,
				}),
			],
			checkboxes: [
				{
					name: 'attractionPlanSupplyManpower',
					type: CheckboxType.MULTIPLE,
					options: ['1', '2', '3', '4'],
					classNames: [
						'absolute top-[155px] left-[420px] w-[264px] flex justify-center',
						'absolute top-[185px] left-[420px] w-[264px] flex justify-center',
						'absolute top-[214px] left-[420px] w-[264px] flex justify-center',
						'absolute top-[244px] left-[420px] w-[264px] flex justify-center',
					],
					iconType: 'circle',
				},
				{
					name: 'attractionPlanManpowerManagement',
					type: CheckboxType.MULTIPLE,
					options: ['1', '2'],
					classNames: [
						'absolute top-[346px] left-[315px]',
						'absolute top-[346px] left-[606px]',
					],
					iconType: 'circle',
				},
			],
		},
		{
			background: {
				src: Images.attractionPlan3Highlight,
				className: 'template',
				dataSource: Images.attractionPlan3,
			},
			textAreas: [
				{
					name: 'attractionPlanTrainingPeriod',
					maxHeight: 40,
					maxLength: 30,
					containerClassName: 'absolute w-[186px] top-[182px] left-[86px]',
					regex: REGEX.ALLOW_KR_EN_NUMBER,
				},
				{
					name: 'attractionPlanTrainingMethod',
					maxHeight: 40,
					maxLength: 30,
					containerClassName: 'absolute w-[186px] top-[182px] left-[289px]',
					regex: REGEX.ALLOW_KR_EN_NUMBER,
				},
				{
					name: 'attractionPlanTrainingDetail',
					maxHeight: 40,
					maxLength: 30,
					containerClassName: 'absolute w-[186px] top-[182px] left-[492px]',
					regex: REGEX.ALLOW_KR_EN_NUMBER,
				},
				(values, index) => ({
					name: 'attractionPlanPreventionUnauthDept',
					maxHeight: 44,
					maxLength: 100,
					containerClassName: cn(
						'absolute w-[414px] top-[286px] left-[265px]',
						values?.attractionPlanPreventionUnauthDept && 'bg-white'
					),
					regex: REGEX.ALLOW_KR_EN_NUMBER_SPECIAL_CHARS,
				}),
				{
					name: 'attractionPlanPreventionUnauthDeptForeigner',
					maxHeight: 28,
					maxLength: 100,
					containerClassName: 'absolute w-[414px] top-[333px] left-[268px]',
					regex: REGEX.ALLOW_KR_EN_NUMBER_SPECIAL_CHARS,
				},
				{
					name: 'attractionPlanPreventionUnauthDeptEmployer',
					maxHeight: 28,
					maxLength: 100,
					containerClassName: 'absolute w-[414px] top-[363px] left-[268px]',
					regex: REGEX.ALLOW_KR_EN_NUMBER_SPECIAL_CHARS,
				},
				{
					name: 'attractionPlanPreventionUnauthDeptGovernment',
					maxHeight: 28,
					maxLength: 100,
					containerClassName: 'absolute w-[414px] top-[393px] left-[268px]',
					regex: REGEX.ALLOW_KR_EN_NUMBER_SPECIAL_CHARS,
				},
				(values, index) => ({
					name: 'attractionPlanPreventionUnauthDeptEtc',
					maxHeight: 28,
					maxLength: 100,
					containerClassName: cn(
						'absolute w-[414px] top-[424px] left-[265px]',
						values?.attractionPlanPreventionUnauthDeptEtc && 'bg-white'
					),
					regex: REGEX.ALLOW_KR_EN_NUMBER_SPECIAL_CHARS,
				}),
				// {
				// 	name: 'attractionPlanCovid19PlanVaccination',
				// 	maxHeight: 40,
				// 	maxLength: 100,
				// 	containerClassName: 'absolute w-[491px] top-[533px] left-[189px]',
				// 	regex: REGEX.ALLOW_KR_EN_NUMBER_SPECIAL_CHARS,
				// },
				// (values, index) => ({
				// 	name: 'attractionPlanCovid19PlanFacility',
				// 	maxHeight: 40,
				// 	maxLength: 100,
				// 	containerClassName: cn(
				// 		'absolute w-[491px] top-[578px] left-[189px]',
				// 		values?.attractionPlanCovid19PlanFacility && 'bg-white'
				// 	),
				// 	regex: REGEX.ALLOW_KR_EN_NUMBER_SPECIAL_CHARS,
				// }),
				// {
				// 	name: 'attractionPlanCovid19PlanBear',
				// 	maxHeight: 40,
				// 	maxLength: 100,
				// 	containerClassName: 'absolute w-[491px] top-[622px] left-[189px]',
				// 	regex: REGEX.ALLOW_KR_EN_NUMBER_SPECIAL_CHARS,
				// },
				// {
				// 	name: 'attractionPlanCovid19PlanAction',
				// 	maxHeight: 40,
				// 	maxLength: 100,
				// 	containerClassName: 'absolute w-[491px] top-[666px] left-[189px]',
				// 	regex: REGEX.ALLOW_KR_EN_NUMBER_SPECIAL_CHARS,
				// },
			],
		},
	],
};
